import {
  analysisTypeStyle,
  formatRoi,
  raceMatrixCellClass,
  raceMatrixCellTextClass,
  modelLabel,
  modelShortCode,
  RaceMatrixData,
} from '../lib/history-performance';

interface Props {
  data: RaceMatrixData;
  /** Hide the colour legend (e.g. when shown compactly inside a meeting tile). */
  showLegend?: boolean;
}

/**
 * Slots × races grid: each cell is a coloured square (green/yellow/red/none) with
 * the 2-char code of the model that produced that race+slot. Summary columns show
 * top-pick winners, all-tip winners, and top/value ROI per slot. Renders model
 * codes on grey cells before results exist, so it doubles as a "which model is in
 * each slot" view. Ported from docker/admin/src/components/RaceMatrixTable.tsx.
 */
export function RaceMatrixTable({ data, showLegend = true }: Props) {
  return (
    <>
      {showLegend && (
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-4 w-4 rounded-sm bg-emerald-500" />
            Top pick won
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-4 w-4 rounded-sm bg-yellow-400" />
            Tip won (not top)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-4 w-4 rounded-sm bg-red-400" />
            No winner
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-4 w-4 rounded-sm border border-border bg-muted" />
            No data
          </span>
          <span>Cell code = model used for that race (hover for full name)</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="border-collapse text-xs">
          <thead>
            <tr>
              <th className="min-w-[160px] whitespace-nowrap pb-2 pr-3 text-left font-medium text-muted-foreground">
                Slot / Models
              </th>
              {data.races.map((r) => (
                <th key={r} className="w-9 pb-2 text-center font-medium text-muted-foreground">
                  R{r}
                </th>
              ))}
              <th className="whitespace-nowrap pb-2 pl-3 text-right font-medium text-muted-foreground">Top Picks</th>
              <th className="whitespace-nowrap pb-2 pl-2 text-right font-medium text-muted-foreground">All Tips</th>
              <th className="whitespace-nowrap pb-2 pl-2 text-right font-medium text-muted-foreground">Top ROI</th>
              <th className="whitespace-nowrap pb-2 pl-2 text-right font-medium text-muted-foreground">Val ROI</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.slots.map((slot) => {
              const typeStyle = analysisTypeStyle(slot.analysisTypeId);
              const topRoi = formatRoi(slot.topPickRoi);
              const valRoi = formatRoi(slot.valueRoi);
              const isMixed = slot.models.length > 1;
              return (
                <tr key={slot.analysisTypeId} className={typeStyle.row}>
                  <td className="whitespace-nowrap py-2 pr-3 align-top">
                    <div className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${typeStyle.badge}`}>
                        T{slot.analysisTypeId}
                      </span>
                      {isMixed && (
                        <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                          MIXED
                        </span>
                      )}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {slot.models.length > 0 ? (
                        slot.models.map((m, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <span className="inline-flex w-6 justify-center rounded bg-muted px-1 font-mono font-semibold text-foreground">
                              {modelShortCode(m.aiModel, m.aiVersion)}
                            </span>
                            <span>{modelLabel(m.aiModel, m.aiVersion)}</span>
                            <span>×{m.races}</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-[11px] text-muted-foreground">—</span>
                      )}
                    </div>
                  </td>
                  {data.races.map((r) => {
                    const cell = slot.cells[r] ?? { status: 'none' as const, top1Pos: null, aiModel: null, aiVersion: null };
                    const hasModel = cell.aiModel != null || cell.aiVersion != null;
                    const code = hasModel ? modelShortCode(cell.aiModel, cell.aiVersion) : '';
                    const title = hasModel
                      ? `R${r}: ${modelLabel(cell.aiModel, cell.aiVersion)} — ${cell.status}${
                          cell.top1Pos != null ? ` (top pick pos ${cell.top1Pos})` : ''
                        }`
                      : `R${r}: no analysis in this slot`;
                    return (
                      <td key={r} className="w-9 py-1.5 text-center">
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-sm font-mono text-[10px] font-semibold ${raceMatrixCellClass(
                            cell.status
                          )} ${raceMatrixCellTextClass(cell.status)}`}
                          title={title}
                        >
                          {code}
                        </span>
                      </td>
                    );
                  })}
                  <td className="py-2 pl-3 text-right align-top font-semibold">{slot.topPickWinners}</td>
                  <td className="py-2 pl-2 text-right align-top font-semibold">{slot.totalWinners}</td>
                  <td className={`py-2 pl-2 text-right align-top font-semibold ${topRoi.className}`}>{topRoi.text}</td>
                  <td className={`py-2 pl-2 text-right align-top font-semibold ${valRoi.className}`}>{valRoi.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
